import type { ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Decisiones cortas y bloqueantes: confirmar, cancelar, ajustar stock.
 * Un formulario de mas de cinco campos es una pagina, no un modal.
 *
 * Por dentro es Radix y no un div propio. La version escrita a mano movia el
 * foco al abrir y cerraba con Escape, que parece suficiente hasta que se mide:
 * con el modal abierto quedaban 31 controles del fondo alcanzables con Tab.
 * Quien navega con teclado salia del dialogo a la barra lateral de atras sin
 * enterarse, y volver era imposible sin raton.
 *
 * Atrapar el foco bien —incluido el ciclo al llegar al final, devolverlo al
 * boton que abrio, y marcar el fondo como inerte para los lectores de
 * pantalla— son doscientas lineas que ya estan escritas y probadas. La
 * interfaz publica no cambia: ninguna pantalla que use Modal se entera.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  tone = "neutral",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  tone?: "neutral" | "danger";
}) {
  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50"
          style={{ background: "var(--scrim)" }}
        />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-[400px]",
            "-translate-x-1/2 -translate-y-1/2 rounded-lg border border-line",
            "bg-raised p-5 shadow-xl outline-none",
          )}
        >
          <div className="mb-2 flex items-start justify-between gap-3">
            <Dialog.Title
              className={cn("text-ui font-semibold", tone === "danger" && "text-crit")}
            >
              {title}
            </Dialog.Title>
            <Dialog.Close
              aria-label="Cerrar"
              className="-m-1 grid size-7 place-items-center rounded-md text-ink-3 hover:bg-sunk"
            >
              <X className="size-4" />
            </Dialog.Close>
          </div>

          {description ? (
            <Dialog.Description className="mb-4 text-ui text-ink-2">
              {description}
            </Dialog.Description>
          ) : (
            // Radix avisa por consola si un dialogo no describe su proposito.
            // Con el titulo basta, y decirlo explicito evita el ruido.
            <Dialog.Description className="sr-only">{title}</Dialog.Description>
          )}

          {children}
          {footer && <div className="mt-5 flex justify-end gap-2">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/**
 * Contexto sin perder la lista: historial de movimientos, ficha rapida.
 * En movil entra desde abajo, que es donde llega el pulgar.
 */
export function Drawer({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={(v) => !v && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-50"
          style={{ background: "var(--scrim)" }}
        />
        <Dialog.Content
          className={cn(
            "fixed z-50 border-line bg-raised shadow-xl outline-none",
            "inset-x-0 bottom-0 max-h-[80dvh] overflow-y-auto rounded-t-lg border-t",
            "sm:inset-y-0 sm:left-auto sm:right-0 sm:w-[380px] sm:max-h-none sm:rounded-none sm:border-l sm:border-t-0",
          )}
        >
          <header className="sticky top-0 flex items-center justify-between gap-3 border-b border-line-2 bg-raised px-4 py-3">
            <Dialog.Title className="text-ui font-semibold">{title}</Dialog.Title>
            <Dialog.Close
              aria-label="Cerrar"
              className="grid size-7 place-items-center rounded-md text-ink-3 hover:bg-sunk"
            >
              <X className="size-4" />
            </Dialog.Close>
          </header>
          <Dialog.Description className="sr-only">{title}</Dialog.Description>
          <div className="p-4">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
